import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    fetchPageComments,
    closeCommentsSidebar,
    toggleShowResolved,
} from '../../redux/Slices/pageCommentsSlice';
import PageCommentThread from './PageCommentThread';
import { X, CheckCheck } from 'lucide-react';
import './scss/PageCommentsSidebar.scss';

const CARD_WIDTH = 280;
const CARD_GAP = 8;
const CONTENT_GAP = 24; // Gap between page-content right edge and cards

const PageCommentsSidebar = ({ pageId, userRole, scrollContainerRef }) => {
    const dispatch = useDispatch();
    const [cardStyles, setCardStyles] = useState({}); // { markId: { top, left } }
    const cardsContainerRef = useRef(null);
    const cardHeightsRef = useRef({}); // Cache actual card heights
    const rafRef = useRef(null);

    const {
        byPageId,
        activeMarkId,
        sidebarOpen,
        showResolved,
        pendingMark,
    } = useSelector(state => state.pageComments);

    const pageData = byPageId[pageId] || { comments: [], loading: false };
    const { comments } = pageData;
    const canComment = userRole && ['commenter', 'editor', 'owner'].includes(userRole);

    // Fetch comments when opened
    useEffect(() => {
        if (sidebarOpen && pageId) {
            dispatch(fetchPageComments({ pageId }));
        }
    }, [sidebarOpen, pageId, dispatch]);

    // Build thread list
    const openComments = comments.filter(c => !c.isResolved);
    const resolvedComments = comments.filter(c => c.isResolved);

    const hasPendingThread = pendingMark && !comments.some(c => c.markId === pendingMark.markId);
    const pendingThread = hasPendingThread ? {
        markId: pendingMark.markId,
        quotedText: pendingMark.quotedText,
        isPending: true,
    } : null;

    const allThreads = [
        ...(pendingThread ? [pendingThread] : []),
        ...openComments,
        ...(showResolved ? resolvedComments : []),
    ];

    // Measure actual card heights
    const measureCardHeights = useCallback(() => {
        if (!cardsContainerRef.current) return;
        const cards = cardsContainerRef.current.querySelectorAll('.pcf-card-wrapper');
        const heights = {};
        cards.forEach(card => {
            const markId = card.getAttribute('data-mark-id');
            if (markId) {
                heights[markId] = card.getBoundingClientRect().height;
            }
        });
        cardHeightsRef.current = heights;
    }, []);

    // Calculate positions: fixed top from mark viewport position, fixed left from content right edge
    const calculatePositions = useCallback(() => {
        const scrollContainer = scrollContainerRef?.current;
        if (!scrollContainer || allThreads.length === 0) {
            setCardStyles({});
            return;
        }

        // Find the page-content element to anchor cards to its right edge
        const contentEl = scrollContainer.querySelector('.page-content');
        if (!contentEl) return;

        const contentRect = contentEl.getBoundingClientRect();
        const left = contentRect.right + CONTENT_GAP;

        // Check if there's enough room on the right for floating cards
        const viewportWidth = window.innerWidth;
        const hasRoom = (left + CARD_WIDTH) <= viewportWidth;

        if (!hasRoom) {
            // Fallback: use a narrower position or sidebar mode
            // Position at right edge of viewport with some padding
            const fallbackLeft = viewportWidth - CARD_WIDTH - 16;
            calculateFixedPositions(fallbackLeft < contentRect.right - CARD_WIDTH
                ? null // Not enough room at all, will use sidebar fallback
                : fallbackLeft
            );
            return;
        }

        calculateFixedPositions(left);

        function calculateFixedPositions(fixedLeft) {
            if (fixedLeft === null) {
                // Sidebar fallback mode — stack cards vertically
                setCardStyles(prev => {
                    const styles = {};
                    allThreads.forEach(thread => {
                        styles[thread.markId] = { sidebarMode: true };
                    });
                    return styles;
                });
                return;
            }

            measureCardHeights();

            // Map threads to their mark's viewport top, then sort
            const threadsWithTop = allThreads.map(thread => {
                const markEl = scrollContainer.querySelector(`[data-mark-id="${thread.markId}"]`);
                let markTop = null;
                if (markEl) {
                    markTop = markEl.getBoundingClientRect().top;
                }
                return { thread, markTop };
            }).sort((a, b) => {
                if (a.markTop === null && b.markTop === null) return 0;
                if (a.markTop === null) return 1;
                if (b.markTop === null) return -1;
                return a.markTop - b.markTop;
            });

            const newStyles = {};
            let nextAvailableTop = -Infinity; // Track where next card can start

            for (const { thread, markTop } of threadsWithTop) {
                const cardHeight = cardHeightsRef.current[thread.markId] || 100;
                let top = markTop ?? nextAvailableTop;

                // Push down if overlapping with previous card
                if (top < nextAvailableTop) {
                    top = nextAvailableTop;
                }

                newStyles[thread.markId] = { top, left: fixedLeft };
                nextAvailableTop = top + cardHeight + CARD_GAP;
            }

            setCardStyles(newStyles);
        }
    }, [allThreads, scrollContainerRef, measureCardHeights]);

    // Sync positions on scroll, resize, and thread changes
    useEffect(() => {
        if (!sidebarOpen) return;

        const scrollContainer = scrollContainerRef?.current;

        const onUpdate = () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(calculatePositions);
        };

        // Initial calculation
        calculatePositions();

        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', onUpdate, { passive: true });
        }
        window.addEventListener('resize', onUpdate);

        return () => {
            if (scrollContainer) scrollContainer.removeEventListener('scroll', onUpdate);
            window.removeEventListener('resize', onUpdate);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [sidebarOpen, calculatePositions, scrollContainerRef]);

    // Recalculate after DOM settles (new marks, comments loaded)
    useEffect(() => {
        if (sidebarOpen) {
            const timer = setTimeout(calculatePositions, 50);
            return () => clearTimeout(timer);
        }
    }, [sidebarOpen, comments, pendingMark, calculatePositions]);

    // Watch card size changes
    useEffect(() => {
        if (!cardsContainerRef.current || !sidebarOpen) return;

        const observer = new ResizeObserver(() => {
            measureCardHeights();
            calculatePositions();
        });

        const cards = cardsContainerRef.current.querySelectorAll('.pcf-card-wrapper');
        cards.forEach(card => observer.observe(card));
        return () => observer.disconnect();
    }, [sidebarOpen, allThreads.length, measureCardHeights, calculatePositions]);

    if (!sidebarOpen) return null;

    const hasContent = allThreads.length > 0;
    const isSidebarMode = hasContent && Object.values(cardStyles).some(s => s?.sidebarMode);

    return (
        <div
            className={`page-comments-float ${isSidebarMode ? 'sidebar-mode' : ''}`}
            ref={cardsContainerRef}
        >
            {/* Header — only shown in sidebar fallback mode */}
            {isSidebarMode && (
                <div className="pcf-header">
                    <span className="pcf-header-title">Comments</span>
                    <div className="pcf-header-actions">
                        {resolvedComments.length > 0 && (
                            <button
                                className={`pcf-header-btn ${showResolved ? 'active' : ''}`}
                                onClick={() => dispatch(toggleShowResolved())}
                                title={showResolved ? 'Hide resolved' : 'Show resolved'}
                            >
                                <CheckCheck size={14} />
                            </button>
                        )}
                        <button
                            className="pcf-header-btn"
                            onClick={() => dispatch(closeCommentsSidebar())}
                            title="Close comments"
                        >
                            <X size={14} />
                        </button>
                    </div>
                </div>
            )}

            {/* Cards */}
            {hasContent && allThreads.map(thread => {
                const markId = thread.markId;
                const isPending = !!thread.isPending;
                const isResolved = !!thread.isResolved;
                const style = cardStyles[markId] || {};

                const cardStyle = style.sidebarMode
                    ? {} // In sidebar mode, cards flow normally
                    : {
                        position: 'fixed',
                        top: style.top != null ? `${style.top}px` : undefined,
                        left: style.left != null ? `${style.left}px` : undefined,
                        width: `${CARD_WIDTH}px`,
                    };

                return (
                    <div
                        key={isPending ? `pending-${markId}` : thread._id}
                        className={`pcf-card-wrapper ${activeMarkId === markId ? 'active' : ''} ${isPending ? 'new' : ''} ${isResolved ? 'resolved' : ''}`}
                        style={cardStyle}
                        data-mark-id={markId}
                    >
                        <PageCommentThread
                            comment={thread}
                            pageId={pageId}
                            isActive={activeMarkId === markId}
                            canComment={canComment}
                            isPending={isPending}
                            isResolved={isResolved}
                        />
                    </div>
                );
            })}
        </div>
    );
};

export default PageCommentsSidebar;
